import Icon from "../../components/ui/Icon";
import { FC, useContext, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Header from "../../components/ui/Header";
import {
  Button,
  CategoryIcon,
  FlexContent,
  InlineOrderEditor,
  TableCell,
  TableHeadCell,
  TableRow,
  useDebounce,
} from "@rothoblaas/design-system";
import Table from "../../components/ui/Table";
import { getEditAction } from "../../utils/rowActions";
import Visibility from "../../components/ui/Visibility";
import NotificationDispatch, {
  showErrorNotification,
  showSuccessNotification,
} from "../../context/notificationContext";
import useInitializer from "../../hooks/useInitializer";
import useListFilters from "../../hooks/useListFilters";
import useI18n from "../../hooks/useTranslations";
import { SearchModule } from "../../http/types/modules";
import { useModulesApi } from "../../http/useModules";
import { getAllTypes, PaginationQuery } from "../../types";
import AdminCategoriesModal from "../categories/AdminCategoriesModal";

const AdminModules: FC = () => {
  const i18n = useI18n();
  const aborter = useRef(new AbortController());
  const [showModalCategories, setShowModalCategories] = useState(false);
  const modulesApi = useModulesApi();
  const navigate = useNavigate();
  const allTypes = getAllTypes();
  const dispatch = useContext(NotificationDispatch);

  const updateOrder = async (id: string, newOrder: number) => {
    try {
      await modulesApi.updateAdminModuleOrder(id, { order: newOrder });
      dispatch(showSuccessNotification(i18n.translation));
    } catch (err) {
      dispatch(showErrorNotification(i18n, err));
    }
  };

  const filters = useListFilters({ isSearchable: true });
  const debouncedValue = useDebounce(filters.state.q);
  const query: PaginationQuery = {
    ...filters.state,
    q: debouncedValue,
  };

  const { data, error } = modulesApi.useAdminModules(query, aborter.current.signal);

  const isInitialized = useInitializer(data || error);

  if (!data && !error && !isInitialized) {
    return;
  }

  return (
    <>
      {showModalCategories && (
        <AdminCategoriesModal
          onClose={() => {
            setShowModalCategories(false);
          }}
        />
      )}
      <Header title={i18n.translation.modules.plural} path={{ display: i18n.translation.modules.all }} buttons={[]} />

      <FlexContent padding noPaddingTop>
        <Table<SearchModule>
          filters={filters}
          headingActions={
            <Button onClick={() => setShowModalCategories(true)}><Icon glyph={CategoryIcon} />
              {i18n.translation.categories.view}
            </Button>
          }
          search={{
            placeholder: i18n.translation.modules.search,
            onChange: () => {
              aborter.current.abort();
              aborter.current = new AbortController();
            },
          }}
          head={
            <TableRow>
              <TableHeadCell align="left">{i18n.translation.common.name}</TableHeadCell>
              <TableHeadCell align="left">{i18n.translation.common.description}</TableHeadCell>
              <TableHeadCell align="left">{i18n.translation.common.type}</TableHeadCell>
              <TableHeadCell align="left">{i18n.translation.common.visibility}</TableHeadCell>
              <TableHeadCell align="center">{i18n.translation.common.order}</TableHeadCell>
              <TableHeadCell></TableHeadCell>
            </TableRow>
          }
          items={data?.items}
          renderRow={(Module) => (
            <>
              <TableCell>
                <Link onClick={(e) => e.stopPropagation()} to={Module.id}>
                  {Module.name}
                </Link>
              </TableCell>
              <TableCell>{Module.description}</TableCell>
              <TableCell>{allTypes[allTypes.findIndex((t) => t.value === Module.type)]!.label}</TableCell>
              <TableCell>
                <Visibility
                  value={Module.visibility}
                  onChange={(visibility) => {
                    modulesApi
                      .updateAdminModuleSettings(Module.id, { visibility })
                      .then(() => dispatch(showSuccessNotification(i18n.translation)))
                      .catch((err) => dispatch(showErrorNotification(i18n, err)));
                  }}
                />
              </TableCell>
              <TableCell align="center">
                <InlineOrderEditor order={Module.order} onUpdate={(newOrder) => updateOrder(Module.id, newOrder)} />
              </TableCell>
            </>
          )}
          rowActions={(Module) => [getEditAction(i18n, () => navigate(`/admin/modules/${Module.id}`))]}
          onRowClick={(Module) => navigate(`/admin/modules/${Module.id}`)}
          total={data?.total}
          error={!!error}
        />
      </FlexContent>
    </>
  );
};

export default AdminModules;
